/**
 * Learning Packages Service
 * Handles learning package CRUD, per-area item assignment, public access,
 * package analytics, learner self-stats, creator statistics, and the anonymous-key roster.
 */
import api from './apiClient';
import { getAnonymousVisitorKey } from '../utils/anonymousVisitorKey';

/**
 * Get paginated learning packages visible to the current creator.
 * @param {Object} params - Query params (page, pageSize, search, isPublished)
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Paginated learning packages
 */
export const getLearningPackages = (params = {}, options = {}) => api.get('/learning-packages', params, options);

/**
 * Get one learning package for editing.
 * @param {number} id - Learning package ID
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Learning package detail
 */
export const getLearningPackage = (id, options = {}) => api.get(`/learning-packages/${id}`, {}, options);

/**
 * Create a learning package.
 * @param {Object} data - Create payload (name, descriptionRichContent)
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Created package
 */
export const createLearningPackage = (data, options = {}) => api.post('/learning-packages', data, options);

/**
 * Update a learning package.
 * @param {number} id - Learning package ID
 * @param {Object} data - Update payload
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Updated package detail
 */
export const updateLearningPackage = (id, data, options = {}) => api.put(`/learning-packages/${id}`, data, options);

/**
 * Delete a learning package.
 * @param {number} id - Learning package ID
 * @param {Object} options - Request options
 * @returns {Promise<void>}
 */
export const deleteLearningPackage = (id, options = {}) => api.delete(`/learning-packages/${id}`, options);

/**
 * Publish a learning package.
 * @param {number} id - Learning package ID
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Message response
 */
export const publishLearningPackage = (id, options = {}) => api.post(`/learning-packages/${id}/publish`, {}, options);

/**
 * Unpublish a learning package.
 * @param {number} id - Learning package ID
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Message response
 */
export const unpublishLearningPackage = (id, options = {}) => api.post(`/learning-packages/${id}/unpublish`, {}, options);

/**
 * Add an item to one of the package areas.
 * @param {number} id - Learning package ID
 * @param {number} itemType - 0 = LearningPath, 1 = QuizCollection, 2 = TrainingProfile
 * @param {number} itemId - Item ID
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Message response
 */
export const addPackageItem = (id, itemType, itemId, options = {}) => api.post(`/learning-packages/${id}/items`, { itemType, itemId }, options);

/**
 * Remove an item from one of the package areas.
 * @param {number} id - Learning package ID
 * @param {string} itemTypeSlug - learning-path | quiz-collection | training-profile
 * @param {number} itemId - Item ID
 * @param {Object} options - Request options
 * @returns {Promise<void>}
 */
export const removePackageItem = (id, itemTypeSlug, itemId, options = {}) => api.delete(`/learning-packages/${id}/items/${itemTypeSlug}/${itemId}`, options);

/**
 * Reorder the items of one package area.
 * @param {number} id - Learning package ID
 * @param {number} itemType - 0 = LearningPath, 1 = QuizCollection, 2 = TrainingProfile
 * @param {number[]} orderedItemIds - Item IDs in the new display order
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Message response
 */
export const reorderPackageItems = (id, itemType, orderedItemIds, options = {}) => api.put(`/learning-packages/${id}/items/reorder`, { itemType, orderedItemIds }, options);

/**
 * Get items the caller can add to one package area.
 * @param {number} id - Learning package ID
 * @param {string} itemTypeSlug - learning-path | quiz-collection | training-profile
 * @param {Object} options - Request options
 * @returns {Promise<Array>} Available items
 */
export const getAvailablePackageItems = (id, itemTypeSlug, options = {}) => api.get(`/learning-packages/${id}/available-items`, { itemType: itemTypeSlug }, options);

/**
 * Get the public list of published learning packages.
 * @param {Object} options - Request options
 * @returns {Promise<Array>} Published packages
 */
export const getPublicLearningPackages = (options = {}) => api.get('/learning-packages/public', {}, options);

/**
 * Get one published learning package with published items only.
 * @param {number} id - Learning package ID
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Public package detail
 */
export const getPublicLearningPackage = (id, options = {}) => api.get(`/learning-packages/${id}/public`, {}, options);

/**
 * Record a public package-page visit.
 * @param {number} id - Learning package ID
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Message response
 */
export const recordPackageVisit = (id, options = {}) => api.post(`/learning-packages/${id}/analytics/visit`, {
  anonymousVisitorKey: getAnonymousVisitorKey(),
}, options);

/**
 * Record a public item-open event on the package page.
 * @param {number} id - Learning package ID
 * @param {number} itemType - 0 = LearningPath, 1 = QuizCollection, 2 = TrainingProfile
 * @param {number} itemId - Item ID
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Message response
 */
export const recordPackageItemOpen = (id, itemType, itemId, options = {}) => api.post(`/learning-packages/${id}/analytics/item-open`, {
  anonymousVisitorKey: getAnonymousVisitorKey(),
  itemType,
  itemId,
}, options);

/**
 * Get the caller's package-scoped personal dashboard.
 * Authenticated callers are resolved server-side; the visitor key covers anonymous callers.
 * @param {number} id - Learning package ID
 * @param {Object} options - Request options
 * @returns {Promise<Object>} My-stats DTO
 */
export const getMyPackageStats = (id, options = {}) => api.get(`/learning-packages/${id}/my-stats`, {
  visitorKey: getAnonymousVisitorKey(),
}, options);

/**
 * Get aggregated creator statistics for a learning package.
 * @param {number} id - Learning package ID
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Statistics DTO
 */
export const getLearningPackageStatistics = (id, options = {}) => api.get(`/learning-packages/${id}/statistics`, {}, options);

/**
 * Get the participants table with roster-resolved names.
 * @param {number} id - Learning package ID
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Participants DTO
 */
export const getLearningPackageParticipants = (id, options = {}) => api.get(`/learning-packages/${id}/participants`, {}, options);

/**
 * Get a detailed drill-in for one or more combined actors of a package
 * (progress, quiz attempt stats, quest achievements, grouped timeline).
 * @param {number} id - Learning package ID
 * @param {string[]} actorKeys - Actor keys ("user:{id}" / "anon:{key}", max 10)
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Participant details DTO
 */
export const getPackageParticipantDetails = (id, actorKeys, options = {}) => api.get(`/learning-packages/${id}/participant-details`, {
  actorKeys: actorKeys.join(','),
}, options);

/**
 * Bulk-import roster aliases parsed from JSON or CSV.
 * @param {Array<{anonymousVisitorKey: string, displayName: string, note?: string}>} entries - Entries to import
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Import result with created/updated/skipped counts
 */
export const importAnonymousKeyAliases = (entries, options = {}) => api.post('/anonymous-key-aliases/import', { entries }, options);

/**
 * Get the caller's anonymous-key roster.
 * @param {Object} params - Query params (search, page, pageSize)
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Paginated aliases
 */
export const getAnonymousKeyAliases = (params = {}, options = {}) => api.get('/anonymous-key-aliases', params, options);

/**
 * Create a roster alias for an anonymous visitor key.
 * @param {Object} data - Create payload (anonymousVisitorKey, displayName, note)
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Created alias
 */
export const createAnonymousKeyAlias = (data, options = {}) => api.post('/anonymous-key-aliases', data, options);

/**
 * Update a roster alias.
 * @param {number} id - Alias ID
 * @param {Object} data - Update payload (displayName, note, clearNote)
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Updated alias
 */
export const updateAnonymousKeyAlias = (id, data, options = {}) => api.put(`/anonymous-key-aliases/${id}`, data, options);

/**
 * Delete a roster alias.
 * @param {number} id - Alias ID
 * @param {Object} options - Request options
 * @returns {Promise<void>}
 */
export const deleteAnonymousKeyAlias = (id, options = {}) => api.delete(`/anonymous-key-aliases/${id}`, options);

/**
 * Package item type values matching the backend PackageItemType enum.
 */
export const PackageItemTypes = {
  LearningPath: 0,
  QuizCollection: 1,
  TrainingProfile: 2,
};

/**
 * Route slugs for item types used in delete/available-items endpoints.
 */
export const PackageItemTypeSlugs = {
  [PackageItemTypes.LearningPath]: 'learning-path',
  [PackageItemTypes.QuizCollection]: 'quiz-collection',
  [PackageItemTypes.TrainingProfile]: 'training-profile',
};

const learningPackagesService = {
  getLearningPackages,
  getLearningPackage,
  createLearningPackage,
  updateLearningPackage,
  deleteLearningPackage,
  publishLearningPackage,
  unpublishLearningPackage,
  addPackageItem,
  removePackageItem,
  reorderPackageItems,
  getAvailablePackageItems,
  getPublicLearningPackages,
  getPublicLearningPackage,
  recordPackageVisit,
  recordPackageItemOpen,
  getMyPackageStats,
  getLearningPackageStatistics,
  getLearningPackageParticipants,
  getPackageParticipantDetails,
  importAnonymousKeyAliases,
  getAnonymousKeyAliases,
  createAnonymousKeyAlias,
  updateAnonymousKeyAlias,
  deleteAnonymousKeyAlias,
  PackageItemTypes,
  PackageItemTypeSlugs,
};

export default learningPackagesService;
